import { WORLD } from "../config";
import { harbourWaterSdf, landSdf } from "../coast";
import { BERTHS } from "../boats";

export const SOUTH_QUAY: Array<[number, number]> = [
  [-26, -22],
  [-12, -30],
  [4, -32],
  [20, -28],
  [34, -18],
];

export const WEST_PONTOON: Array<[number, number]> = [
  [-38, -12],
  [-40, 4],
  [-38, 20],
  [-30, 32],
];

export const EAST_PONTOON: Array<[number, number]> = [
  [36, -18],
  [38, -2],
  [36, 14],
  [30, 28],
];

export type Spot = { x: number; y: number; z: number };

function distToSeg(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax;
  const vz = bz - az;
  const l2 = vx * vx + vz * vz || 1;
  const t = Math.min(1, Math.max(0, ((px - ax) * vx + (pz - az) * vz) / l2));
  return Math.hypot(px - (ax + t * vx), pz - (az + t * vz));
}

export function distToPoly(x: number, z: number, poly: Array<[number, number]>): number {
  let d = Infinity;
  for (let i = 0; i < poly.length - 1; i++) {
    d = Math.min(d, distToSeg(x, z, poly[i][0], poly[i][1], poly[i + 1][0], poly[i + 1][1]));
  }
  return d;
}

export function onDeck(x: number, z: number): boolean {
  return (
    distToPoly(x, z, SOUTH_QUAY) < 3.6 ||
    distToPoly(x, z, WEST_PONTOON) < 1.5 ||
    distToPoly(x, z, EAST_PONTOON) < 1.5
  );
}

export function isWalkable(x: number, z: number): boolean {
  if (onDeck(x, z)) return true;
  return landSdf(x, z) < -1.6;
}

export function inHarbourWater(x: number, z: number): boolean {
  return harbourWaterSdf(x, z) < -1.2 && !onDeck(x, z);
}

export const BIRD_PERCHES: Spot[] = [
  { x: -26, y: WORLD.quayHeight + 0.12, z: -22 },
  { x: 4, y: WORLD.quayHeight + 0.12, z: -32 },
  { x: 34, y: WORLD.quayHeight + 0.12, z: -18 },
  { x: -40, y: WORLD.quayHeight + 0.2, z: 4 },
  { x: -38, y: WORLD.quayHeight + 0.2, z: 20 },
  { x: -30, y: WORLD.quayHeight + 0.2, z: 32 },
  { x: 38, y: WORLD.quayHeight + 0.2, z: -2 },
  { x: 36, y: WORLD.quayHeight + 0.2, z: 14 },
  { x: 30, y: WORLD.quayHeight + 0.2, z: 28 },
  { x: 0, y: 9.1, z: -41 },
  { x: -14, y: 9.1, z: -40 },
  { x: 14, y: 9.1, z: -40 },
  { x: -4, y: 3.5, z: 74 },
  { x: 34, y: 3.5, z: 72 },
  { x: -32, y: 2.1, z: 20 },
  { x: 30, y: 2.1, z: 16 },
  { x: -6, y: 0.55, z: 70 },
  { x: 32, y: 0.55, z: 68 },
];

export const DUCK_SITS: Spot[] = [
  { x: -38, y: WORLD.quayHeight + 0.22, z: -8 },
  { x: -40, y: WORLD.quayHeight + 0.22, z: 8 },
  { x: -36, y: WORLD.quayHeight + 0.22, z: 24 },
  { x: 36, y: WORLD.quayHeight + 0.22, z: -12 },
  { x: 38, y: WORLD.quayHeight + 0.22, z: 4 },
  { x: -28, y: 0.35, z: -16 },
  { x: 30, y: 0.35, z: -20 },
  { x: -20, y: 0.4, z: -24 },
  { x: 16, y: 0.35, z: -26 },
  { x: -42, y: 0.45, z: 14 },
];

export function duckWaterSpots(): Array<[number, number]> {
  const spots: Array<[number, number]> = [
    [-32, -6],
    [-34, 8],
    [-30, 22],
    [-18, 14],
    [-12, -18],
    [6, -22],
    [18, -16],
    [28, -8],
    [32, 6],
    [24, 18],
    [-8, 6],
    [8, 4],
  ];
  for (const b of BERTHS) {
    spots.push([b.x + Math.cos(b.heading) * 3.5, b.z - Math.sin(b.heading) * 3.5]);
    spots.push([b.x - Math.cos(b.heading) * 2.2, b.z + Math.sin(b.heading) * 2.2]);
  }
  return spots.filter(([x, z]) => harbourWaterSdf(x, z) < -1.5);
}

export function swimLanes(): Array<Array<[number, number]>> {
  return [
    [
      [-22, 6],
      [-14, 10],
      [-8, 4],
      [-18, 0],
    ],
    [
      [8, -4],
      [16, 2],
      [10, 10],
      [2, 6],
    ],
    [
      [-28, 16],
      [-20, 20],
      [-12, 14],
      [-22, 10],
    ],
  ];
}
